import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { addToCart, CartLine, cartTotal, setQty, toOrderBody, unitPrice } from '../lib/cart';
import { navigate } from '../lib/router';
import { baht, main, second } from '../lib/text';
import type { Menu, MenuItem } from '../lib/types';
import { DishSheet } from '../guest/DishSheet';
import type { GuestState } from '../guest/GuestApp';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

/** /order/<sessionId>: staff take an order for a table or a takeout (same menu, same options, sent as staff). */
export function OrderPad({ path, sessionId }: { path: string; sessionId: string }) {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [label, setLabel] = useState('');
  const [cat, setCat] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [open, setOpen] = useState<MenuItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { s, online } = useStaff(() => undefined, () => undefined);

  const load = useCallback(async () => {
    if (!s) return;
    try {
      const [m, sess] = await Promise.all([
        api<Menu>('GET', '/menu', { token: s.token }),
        api<{ tableName: string; kind?: string; status: string }>('GET', `/sessions/${sessionId}`, { token: s.token }),
      ]);
      setMenu(m);
      setCat((c) => c || m.categories[0]?.id || '');
      setLabel(`${sess.kind === 'takeout' ? 'กลับบ้าน' : 'โต๊ะ'} ${sess.tableName}`);
      if (sess.status !== 'open') setErr('This sitting is closed');
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, [s, sessionId]);
  useEffect(() => { void load(); }, [load]);

  const items = useMemo(() => new Map((menu?.categories ?? []).flatMap((c) => c.items).map((i) => [i.id, i])), [menu]);
  const soldOut = useMemo(() => new Set(menu?.soldOut ?? []), [menu]);
  const shim = { soldOut, guest: { tableName: label }, add: (id: string, ch: Record<string, string[]>, q: number, note?: string) => setCart((c) => addToCart(c, id, ch, q, note)) } as unknown as GuestState;

  const send = async () => {
    if (!s || !cart.length) return;
    setBusy(true);
    setErr(null);
    try {
      await api('POST', `/sessions/${sessionId}/orders`, { token: s.token, body: toOrderBody(cart) });
      setCart([]);
      navigate('/floor');
    } catch (e) { handleAuthError(e); setErr((e as Error).message); void load(); } finally { setBusy(false); }
  };

  return (
    <Shell path={path} online={online} sub={label}>
      <div className="layout">
        <section className="main">
          <nav className="chips" style={{ padding: '0 0 12px' }} aria-label="Categories">
            {(menu?.categories ?? []).map((c) => <button key={c.id} className="chip" aria-pressed={c.id === cat} onClick={() => setCat(c.id)}>{main(c.name)}</button>)}
          </nav>
          <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
            {(menu?.categories.find((c) => c.id === cat)?.items ?? []).map((it) => {
              const sold = soldOut.has(it.id);
              return (
                <button key={it.id} className="tile free" disabled={sold} style={{ minHeight: 96, opacity: sold ? 0.5 : 1 }}
                  onClick={() => ((it.options ?? []).length ? setOpen(it) : setCart((c) => addToCart(c, it.id, {}, 1)))}>
                  <span className="name-th">{main(it.name)}</span>
                  <span className="name-en">{second(it.name)}</span>
                  <span className="price">{sold ? 'หมด · Sold out' : baht(unitPrice(it, {}))}</span>
                </button>
              );
            })}
          </div>
        </section>
        <aside className="aside panel side">
          <h2 className="display" style={{ margin: 0, fontSize: 20 }}>{label}</h2>
          {cart.length === 0 && <span style={{ color: '#A9AFC6' }}>แตะเมนูเพื่อเพิ่ม · Tap dishes to add</span>}
          {cart.map((l) => {
            const it = items.get(l.itemId);
            if (!it) return null;
            const extras = (it.options ?? []).flatMap((g) => (l.choices[g.id] ?? []).map((id) => main(g.choices.find((c) => c.id === id)?.name)));
            return (
              <div key={l.key} className="row">
                <span>{main(it.name)}{(extras.length || l.note) ? <span style={{ fontSize: 12, color: '#A9AFC6' }}> · {[...extras, l.note].filter(Boolean).join(', ')}</span> : null}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button className="btn-dark" style={{ minHeight: 36, padding: '0 10px' }} onClick={() => setCart((c) => setQty(c, l.key, l.qty - 1))} aria-label="One less">−</button>
                  <b>{l.qty}</b>
                  <button className="btn-dark" style={{ minHeight: 36, padding: '0 10px' }} onClick={() => setCart((c) => setQty(c, l.key, l.qty + 1))} aria-label="One more">+</button>
                </span>
              </div>
            );
          })}
          <div className="row display"><span>รวม</span><span style={{ color: 'var(--mango)' }}>{baht(cartTotal(cart, items))}</span></div>
          {err && <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>{err}</div>}
          <button className="btn-mango" disabled={busy || !cart.length} onClick={() => void send()}>ส่งเข้าครัว · Send to kitchen</button>
          <button className="btn-dark" onClick={() => navigate('/floor')}>กลับ · Back to floor</button>
        </aside>
      </div>
      {open && <DishSheet item={open} s={shim} onClose={() => setOpen(null)} />}
    </Shell>
  );
}
